<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/apiService";

// Components
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonAlert from "@/components/ArgonAlert.vue";
import BackendPagination from "@/components/BackendPagination.vue";

// State variables
const categories = ref([]);
const errorMessage = ref("");
const successMessage = ref("");
const loading = ref(true);
const showModal = ref(false);
const isEditing = ref(false);
const selectedCategory = ref(null);

// Pagination
const currentPage = ref(1);
const itemsPerPage = 10;
const totalItems = ref(0);

// Router
const router = useRouter();

// Computed
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage));

// Fetch categories
const fetchCategories = async () => {
  loading.value = true;
  try {
    const response = await api.get(`/categories?page=${currentPage.value}`);
    categories.value = response.data.member || [];
    totalItems.value = response.data.totalItems || 0;
  } catch (error) {
    errorMessage.value = error.response?.data?.message || "Error al obtener categorías.";
  } finally {
    loading.value = false;
  }
};

// Pagination handler
const changePage = async (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    await fetchCategories();
  }
};

// Modal handlers
const openModal = () => {
  isEditing.value = false;
  selectedCategory.value = { type: "" };
  showModal.value = true;
};

const openEditModal = (category) => {
  isEditing.value = true;
  selectedCategory.value = {
    "@id": category["@id"],
    type: category.type || "",
  };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedCategory.value = null;
  errorMessage.value = "";
};

// Save (create or update)
const saveCategory = async () => {
  try {
    if (!selectedCategory.value.type) {
      errorMessage.value = "El nombre de la categoría es obligatorio.";
      return;
    }

    if (isEditing.value) {
      const categoryId = selectedCategory.value["@id"].replace(/^\/api/, "");
      await api.patch(categoryId, {
        type: selectedCategory.value.type,
      });
      successMessage.value = "Categoría actualizada correctamente.";
    } else {
      const response = await api.post("/categories", {
        type: selectedCategory.value.type,
      });
      if (response?.data) {
        categories.value.push(response.data);
      }
      successMessage.value = `Categoría "${selectedCategory.value.type}" agregada correctamente.`;
    }

    fetchCategories();
    closeModal();

    setTimeout(() => {
      successMessage.value = "";
      errorMessage.value = "";
    }, 3000);
  } catch (error) {
    console.error("❌ Error al guardar categoría:", error.response?.data || error.message);
    errorMessage.value = error.response?.data?.message || "Error al guardar la categoría.";
    setTimeout(() => {
      errorMessage.value = "";
    }, 3000);
  }
};

// Delete category
const deleteCategory = async (category) => {
  if (!confirm(`¿Eliminar la categoría "${category.type}"?`)) return;

  try {
    const categoryId = category["@id"].replace(/^\/api/, "");
    await api.delete(categoryId);
    fetchCategories();

    successMessage.value = `Categoría "${category.type}" eliminada correctamente.`;

    setTimeout(() => {
      successMessage.value = "";
    }, 3000);
  } catch (error) {
    console.error("❌ Error al eliminar categoría:", error.response?.data || error.message);
    errorMessage.value = error.response?.data?.message || "Error al eliminar la categoría.";
    setTimeout(() => {
      errorMessage.value = "";
    }, 3000);
  }
};

// On mount
onMounted(() => {
  const token = localStorage.getItem("auth_token");
  if (!token) {
    router.push("/signin");
  } else {
    fetchCategories();
  }
});
</script>



<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex">
            <h6>Categorías</h6>
            <argon-button color="primary" size="sm" class="ms-auto" @click="openModal">
              <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
              Agregar Categoría
            </argon-button>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <div v-if="loading" class="text-center py-3">
                <p class="text-secondary">Cargando categorías...</p>
              </div>

              <argon-alert v-if="successMessage" color="alert-success" icon="ni ni-check-bold" class="mb-2 text-dark w-50 ms-3">
                {{ successMessage }}
              </argon-alert>

              <argon-alert v-if="errorMessage && !showModal" color="alert-danger" icon="ni ni-fat-remove" class="mb-2 text-dark w-50 ms-3">
                {{ errorMessage }}
              </argon-alert>

              <div v-if="categories.length === 0 && !loading && !errorMessage" class="text-center py-3">
                <img src="@/assets/img/illustrations/noData.png" alt="No hay datos disponibles" width="300" />
                <p class="text-secondary">No se encontraron categorías</p>
              </div>

              <table v-if="categories.length > 0" class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Type</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="category in categories" :key="category['@id']">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ category.type }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <font-awesome-icon :icon="['fas', 'pen-to-square']" class="me-3" @click="openEditModal(category)" style="cursor:pointer;" />
                      <font-awesome-icon :icon="['fas', 'trash']" @click="deleteCategory(category)" style="cursor:pointer;" />
                    </td>
                  </tr>
                </tbody>
              </table>

              <BackendPagination
  :current-page="currentPage"
  :total-pages="totalPages"
  @change-page="changePage"
/>

            </div>
          </div>
        </div>

        <!-- Modal -->
        <teleport to="body">
          <div v-if="showModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
              <h5 class="mb-3">{{ isEditing ? "Editar Categoría" : "Agregar Categoría" }}</h5>
              <argon-alert v-if="showModal && errorMessage" color="alert-danger" icon="ni ni-fat-remove" class="mb-3">
                {{ errorMessage }}
              </argon-alert>
              <div class="mb-3">
                <label>Nombre de la Categoría</label>
                <input v-model="selectedCategory.type" class="form-control" type="text" required />
              </div>
              <div class="d-flex justify-content-end">
                <argon-button color="secondary" class="me-2" @click="closeModal">Cancelar</argon-button>
                <argon-button color="primary" @click="saveCategory">
                  {{ isEditing ? "Guardar Cambios" : "Agregar" }}
                </argon-button>
              </div>
            </div>
          </div>
        </teleport>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1051;
  width: 500px;
  max-width: 90%;
  position: relative;
}
</style>